/* tslint:disable */
/* eslint-disable */
/**
 * CyberWell Aegis API
 * Multi-tenant case management API with JWT auth, RBAC (Casbin), audit logging, and tenant isolation.
 *
 * The version of the OpenAPI document: v1
 * 
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

import { mapValues } from '../runtime';
import type { CaseEntityDto } from './CaseEntityDto';
import {
    CaseEntityDtoFromJSON,
    CaseEntityDtoFromJSONTyped,
    CaseEntityDtoToJSON,
    CaseEntityDtoToJSONTyped,
} from './CaseEntityDto';
import type { CaseSourceDto } from './CaseSourceDto';
import {
    CaseSourceDtoFromJSON,
    CaseSourceDtoFromJSONTyped,
    CaseSourceDtoToJSON,
    CaseSourceDtoToJSONTyped,
} from './CaseSourceDto';
import type { CreateCaseEventRequest } from './CreateCaseEventRequest';
import {
    CreateCaseEventRequestFromJSON,
    CreateCaseEventRequestFromJSONTyped,
    CreateCaseEventRequestToJSON,
    CreateCaseEventRequestToJSONTyped,
} from './CreateCaseEventRequest';

/**
 * Case creation request containing all necessary data.
 * 
 * **Required Fields:**
 * - `name`: Case name/title (required)
 * - `severity`: Case severity level (required)
 * 
 * **Optional Fields:**
 * - `summary`: Detailed case description
 * - `case_type_id`: ID of the case type
 * - `owner`: Case owner/assignee
 * - `entities`: List of entities associated with the case
 * - `custom_fields`: Custom fields for the case
 * - `sources`: List of sources for this case
 * - `correlation_group`: Group for linking related cases
 * - `correlation_metadata`: Metadata for case correlation
 * - `source_id`: Source ID from external system
 * - `source_technology`: Source technology that generated this case
 * 
 * **Validation:**
 * - Name cannot be null or empty
 * - Severity must be a valid enum value (CRITICAL, HIGH, MEDIUM, LOW)
 * - Case type ID must reference a valid case type
 * 
 * @export
 * @interface CreateCaseRequest
 */
export interface CreateCaseRequest {
    /**
     * Name/title of the case
     * @type {string}
     * @memberof CreateCaseRequest
     */
    name: string;
    /**
     * Detailed summary of the case
     * @type {string}
     * @memberof CreateCaseRequest
     */
    summary?: string;
    /**
     * Severity level of the case
     * @type {string}
     * @memberof CreateCaseRequest
     */
    severity: CreateCaseRequestSeverityEnum;
    /**
     * ID of the case type (optional for external system cases)
     * @type {string}
     * @memberof CreateCaseRequest
     */
    caseTypeId?: string;
    /**
     * Owner/assignee of the case
     * @type {string}
     * @memberof CreateCaseRequest
     */
    owner?: string;
    /**
     * List of entities associated with the case
     * @type {Array<CaseEntityDto>}
     * @memberof CreateCaseRequest
     */
    entities?: Array<CaseEntityDto>;
    /**
     * Custom fields for the case
     * @type {{ [key: string]: any; }}
     * @memberof CreateCaseRequest
     */
    customFields?: { [key: string]: any; };
    /**
     * List of sources for this case
     * @type {Array<CaseSourceDto>}
     * @memberof CreateCaseRequest
     */
    sources?: Array<CaseSourceDto>;
    /**
     * Correlation group for linking related cases
     * @type {string}
     * @memberof CreateCaseRequest
     */
    correlationGroup?: string;
    /**
     * Correlation metadata for linking related cases
     * @type {{ [key: string]: any; }}
     * @memberof CreateCaseRequest
     */
    correlationMetadata?: { [key: string]: any; };
    /**
     * General case metadata for flexible storage of case-specific information
     * @type {{ [key: string]: any; }}
     * @memberof CreateCaseRequest
     */
    metadata?: { [key: string]: any; };
    /**
     * Source ID from external system
     * @type {string}
     * @memberof CreateCaseRequest
     */
    sourceId?: string;
    /**
     * Source technology that generated this case
     * @type {string}
     * @memberof CreateCaseRequest
     */
    sourceTechnology?: string;
    /**
     * Case status (OPEN, IN_PROGRESS, ESCALATED, RESOLVED)
     * @type {string}
     * @memberof CreateCaseRequest
     */
    status?: CreateCaseRequestStatusEnum;
    /**
     * Case creation time (if not provided, uses current time)
     * @type {Date}
     * @memberof CreateCaseRequest
     */
    created?: Date;
    /**
     * Original creation time from external system (for synchronization)
     * @type {Date}
     * @memberof CreateCaseRequest
     */
    originalCreatedTime?: Date;
    /**
     * Assignment time (when case was assigned to owner)
     * @type {Date}
     * @memberof CreateCaseRequest
     */
    assignedTime?: Date;
    /**
     * Close time (when case was closed/resolved)
     * @type {Date}
     * @memberof CreateCaseRequest
     */
    closedTime?: Date;
    /**
     * Escalation time (when case was escalated)
     * @type {Date}
     * @memberof CreateCaseRequest
     */
    escalatedTime?: Date;
    /**
     * Notification SLA timer value
     * @type {number}
     * @memberof CreateCaseRequest
     */
    notifySLATimer?: number;
    /**
     * Detection SLA timer value
     * @type {number}
     * @memberof CreateCaseRequest
     */
    detectionSLATimer?: number;
    /**
     * Assignment SLA timer value
     * @type {number}
     * @memberof CreateCaseRequest
     */
    assignmentSLATimer?: number;
    /**
     * Remediation SLA timer value
     * @type {number}
     * @memberof CreateCaseRequest
     */
    remediationSLATimer?: number;
    /**
     * Duration SLA timer value
     * @type {number}
     * @memberof CreateCaseRequest
     */
    durationSLATimer?: number;
    /**
     * Close reason details (for cases created in RESOLVED status)
     * @type {{ [key: string]: any; }}
     * @memberof CreateCaseRequest
     */
    closeReason?: { [key: string]: any; };
    /**
     * Close rationale/justification (for cases created in RESOLVED status)
     * @type {string}
     * @memberof CreateCaseRequest
     */
    closeRationale?: string;
    /**
     * Feedback about the case handling process
     * @type {string}
     * @memberof CreateCaseRequest
     */
    feedback?: string;
    /**
     * Creator of timeline events (if not provided, uses authenticated user)
     * @type {string}
     * @memberof CreateCaseRequest
     */
    timelineEventCreator?: string;
    /**
     * List of events/notes for the case (for complete case creation)
     * @type {Array<CreateCaseEventRequest>}
     * @memberof CreateCaseRequest
     */
    events?: Array<CreateCaseEventRequest>;
}


/**
 * @export
 */
export const CreateCaseRequestSeverityEnum = {
    Critical: 'CRITICAL',
    High: 'HIGH',
    Medium: 'MEDIUM',
    Low: 'LOW',
    Info: 'INFO'
} as const;
export type CreateCaseRequestSeverityEnum = typeof CreateCaseRequestSeverityEnum[keyof typeof CreateCaseRequestSeverityEnum];

/**
 * @export
 */
export const CreateCaseRequestStatusEnum = {
    Open: 'OPEN',
    InProgress: 'IN_PROGRESS',
    Escalated: 'ESCALATED',
    Resolved: 'RESOLVED'
} as const;
export type CreateCaseRequestStatusEnum = typeof CreateCaseRequestStatusEnum[keyof typeof CreateCaseRequestStatusEnum];


/**
 * Check if a given object implements the CreateCaseRequest interface.
 */
export function instanceOfCreateCaseRequest(value: object): value is CreateCaseRequest {
    if (!('name' in value) || value['name'] === undefined) return false;
    if (!('severity' in value) || value['severity'] === undefined) return false;
    return true;
}

export function CreateCaseRequestFromJSON(json: any): CreateCaseRequest {
    return CreateCaseRequestFromJSONTyped(json, false);
}

export function CreateCaseRequestFromJSONTyped(json: any, ignoreDiscriminator: boolean): CreateCaseRequest {
    if (json == null) {
        return json;
    }
    return {
        
        'name': json['name'],
        'summary': json['summary'] == null ? undefined : json['summary'],
        'severity': json['severity'],
        'caseTypeId': json['caseTypeId'] == null ? undefined : json['caseTypeId'],
        'owner': json['owner'] == null ? undefined : json['owner'],
        'entities': json['entities'] == null ? undefined : ((json['entities'] as Array<any>).map(CaseEntityDtoFromJSON)),
        'customFields': json['custom_fields'] == null ? undefined : json['custom_fields'],
        'sources': json['sources'] == null ? undefined : ((json['sources'] as Array<any>).map(CaseSourceDtoFromJSON)),
        'correlationGroup': json['correlation_group'] == null ? undefined : json['correlation_group'],
        'correlationMetadata': json['correlation_metadata'] == null ? undefined : json['correlation_metadata'],
        'metadata': json['metadata'] == null ? undefined : json['metadata'],
        'sourceId': json['source_id'] == null ? undefined : json['source_id'],
        'sourceTechnology': json['source_technology'] == null ? undefined : json['source_technology'],
        'status': json['status'] == null ? undefined : json['status'],
        'created': json['created'] == null ? undefined : (new Date(json['created'])),
        'originalCreatedTime': json['original_created_time'] == null ? undefined : (new Date(json['original_created_time'])),
        'assignedTime': json['assigned_time'] == null ? undefined : (new Date(json['assigned_time'])),
        'closedTime': json['closed_time'] == null ? undefined : (new Date(json['closed_time'])),
        'escalatedTime': json['escalated_time'] == null ? undefined : (new Date(json['escalated_time'])),
        'notifySLATimer': json['notifySLATimer'] == null ? undefined : json['notifySLATimer'],
        'detectionSLATimer': json['detectionSLATimer'] == null ? undefined : json['detectionSLATimer'],
        'assignmentSLATimer': json['assignmentSLATimer'] == null ? undefined : json['assignmentSLATimer'],
        'remediationSLATimer': json['remediationSLATimer'] == null ? undefined : json['remediationSLATimer'],
        'durationSLATimer': json['durationSLATimer'] == null ? undefined : json['durationSLATimer'],
        'closeReason': json['close_reason'] == null ? undefined : json['close_reason'],
        'closeRationale': json['close_rationale'] == null ? undefined : json['close_rationale'],
        'feedback': json['feedback'] == null ? undefined : json['feedback'],
        'timelineEventCreator': json['timeline_event_creator'] == null ? undefined : json['timeline_event_creator'],
        'events': json['events'] == null ? undefined : ((json['events'] as Array<any>).map(CreateCaseEventRequestFromJSON)),
    };
}

export function CreateCaseRequestToJSON(json: any): CreateCaseRequest {
    return CreateCaseRequestToJSONTyped(json, false);
}

export function CreateCaseRequestToJSONTyped(value?: CreateCaseRequest | null, ignoreDiscriminator: boolean = false): any {
    if (value == null) {
        return value;
    }

    return {
        
        'name': value['name'],
        'summary': value['summary'],
        'severity': value['severity'],
        'caseTypeId': value['caseTypeId'],
        'owner': value['owner'],
        'entities': value['entities'] == null ? undefined : ((value['entities'] as Array<any>).map(CaseEntityDtoToJSON)),
        'custom_fields': value['customFields'],
        'sources': value['sources'] == null ? undefined : ((value['sources'] as Array<any>).map(CaseSourceDtoToJSON)),
        'correlation_group': value['correlationGroup'],
        'correlation_metadata': value['correlationMetadata'],
        'metadata': value['metadata'],
        'source_id': value['sourceId'],
        'source_technology': value['sourceTechnology'],
        'status': value['status'],
        'created': value['created'] == null ? undefined : ((value['created']).toISOString()),
        'original_created_time': value['originalCreatedTime'] == null ? undefined : ((value['originalCreatedTime']).toISOString()),
        'assigned_time': value['assignedTime'] == null ? undefined : ((value['assignedTime']).toISOString()),
        'closed_time': value['closedTime'] == null ? undefined : ((value['closedTime']).toISOString()),
        'escalated_time': value['escalatedTime'] == null ? undefined : ((value['escalatedTime']).toISOString()),
        'notifySLATimer': value['notifySLATimer'],
        'detectionSLATimer': value['detectionSLATimer'],
        'assignmentSLATimer': value['assignmentSLATimer'],
        'remediationSLATimer': value['remediationSLATimer'],
        'durationSLATimer': value['durationSLATimer'],
        'close_reason': value['closeReason'],
        'close_rationale': value['closeRationale'],
        'feedback': value['feedback'],
        'timeline_event_creator': value['timelineEventCreator'],
        'events': value['events'] == null ? undefined : ((value['events'] as Array<any>).map(CreateCaseEventRequestToJSON)),
    };
}

